import React, { useState, useMemo, useEffect } from "react";
import FilterIcon from "@assets/svg/FilterIcon.svg?react";
import CloseCrossBlack from "@assets/svg/GreyCross.svg?react";
import AirbnbIcon from "@assets/svg/airbnb.svg?react";
import GathernIcon from "@assets/svg/GathernIcon2.svg?react";
import BookingIcon from "@assets/svg/BookingIcon.svg?react";
import ChannelAgoda from "@assets/svg/ChannelAgoda.svg?react";
import MsoolIcon from "@assets/svg/msool-icon.svg?react";
import DateFilter from "../CommonFilters/DateFilter";
import ChannelFilter, { ChannelOption } from "../CommonFilters/ChannelFilter";
import PriceRangeFilter, {
  PriceRange,
} from "../CommonFilters/PriceRangeFilter";
import NameFilter from "../CommonFilters/NameFilter";
import type { Dayjs } from "dayjs";
import {
  FiltersSection,
  FiltersContainer,
  ClearFiltersContainer,
  ClearFiltersText,
} from "./styles";
import { useTranslation } from "react-i18next";
import EnumFilter from "../CommonFilters/EnumFilter";
import CloseIcon from "@mui/icons-material/Close";

export interface ReservationFiltersState {
  dateRange: [Dayjs | null, Dayjs | null] | null;
  selectedChannels: string[];
  selectedNames: string[];
  priceRange: number[] | null; // Allow null - only set when user explicitly filters
  enum: string[];
}

// Define filter types for the array approach
export type FilterType = "date" | "channel" | "name" | "price" | "enum";

interface ReservationFiltersProps {
  filters: ReservationFiltersState;
  onFiltersChange: (filters: ReservationFiltersState) => void;
  onClearFilters: () => void;
  reservations?: any[]; // Add reservations data prop
  // Array approach for filter visibility
  visibleFilters?: FilterType[];
  DateFilterPlaceholder?: [string, string];
  ChannelFilterLabel?: string;
  PriceRangeFilterLabel?: string;
  EnumFilterLabel?: string;
  enumOptions?: string[];
  onNameSearchChange?: (searchTerm: string) => void; // Add name search callback
  nameFilterPlaceholder?: string;
}

const ReservationFilters: React.FC<ReservationFiltersProps> = ({
  filters,
  onFiltersChange,
  onClearFilters,
  DateFilterPlaceholder,
  ChannelFilterLabel,
  PriceRangeFilterLabel,
  EnumFilterLabel,
  enumOptions = [],
  // Array approach for filter visibility
  visibleFilters = [],
  onNameSearchChange,
  nameFilterPlaceholder,
}) => {
  // Local state for managing dropdown open/close
  const [isChannelFilterOpen, setIsChannelFilterOpen] = useState(false);
  const [isPriceRangeFilterOpen, setIsPriceRangeFilterOpen] = useState(false);
  const [isNameFilterOpen, setIsNameFilterOpen] = useState(false);
  const [isEnumFilterOpen, setIsEnumFilterOpen] = useState(false);
  const [nameSearchTerm, setNameSearchTerm] = useState("");
  const [resetNameSearch, setResetNameSearch] = useState(false);
  const { t } = useTranslation();

  // Set showFilters based on visibleFilters array
  const [showFilters, setShowFilters] = useState({
    date: visibleFilters.includes("date"),
    channel: visibleFilters.includes("channel"),
    name: visibleFilters.includes("name"),
    price: visibleFilters.includes("price"),
    enum: visibleFilters.includes("enum"),
  });

  // Update showFilters when visibleFilters prop changes
  useEffect(() => {
    setShowFilters({
      date: visibleFilters.includes("date"),
      channel: visibleFilters.includes("channel"),
      name: visibleFilters.includes("name"),
      price: visibleFilters.includes("price"),
      enum: visibleFilters.includes("enum"),
    });
  }, [visibleFilters]);

  // Channel options
  const channels: ChannelOption[] = [
    {
      key: "3",
      label: "Booking.com",
      icon: <BookingIcon />,
    },
    {
      key: "1",
      label: "Airbnb",
      icon: <AirbnbIcon />,
    },
    {
      key: "2",
      label: "Gathern",
      icon: <GathernIcon />,
    },
    {
      key: "4",
      label: "ChannelAgoda",
      icon: <ChannelAgoda />,
    },
    {
      key: "6",
      label: "Msool",
      icon: <MsoolIcon />,
    },
  ];

  // Handler functions
  const handleDateRangeChange = (
    dates: [Dayjs | null, Dayjs | null] | null
  ) => {
    onFiltersChange({
      ...filters,
      dateRange: dates,
    });
  };

  const handleChannelsChange = (selectedChannels: string[]) => {
    onFiltersChange({
      ...filters,
      selectedChannels,
    });
  };

  const handleNamesChange = (selectedKeys: string[]) => {
    // For now, we'll use the keys directly as names since we're not passing names array
    onFiltersChange({
      ...filters,
      selectedNames: selectedKeys,
    });
  };

  const handlePriceRangeChange = (priceRange: number[]) => {
    onFiltersChange({
      ...filters,
      priceRange,
    });
  };

  const handleEnumChange = (selectedValues: string[]) => {
    onFiltersChange({
      ...filters,
      enum: selectedValues,
    });
  };

  // Handle name search change
  const handleNameSearchChange = (searchTerm: string) => {
    setNameSearchTerm(searchTerm);
    if (onNameSearchChange) {
      onNameSearchChange(searchTerm);
    }
  };

  // Check if any filter is active
  const isAnyFilterActive =
    filters.selectedChannels.length > 0 ||
    filters.selectedNames.length > 0 ||
    (filters.priceRange !== null && filters.priceRange.length === 2) ||
    filters.enum.length > 0 ||
    filters.dateRange !== null ||
    nameSearchTerm.length > 0;

  // Handle clear filters with name search reset
  const handleClearFilters = () => {
    setResetNameSearch(true);
    setNameSearchTerm(""); // Reset the name search term state
    // Reset the reset flag after a short delay
    setTimeout(() => setResetNameSearch(false), 100);
    onClearFilters();
  };

  // Determine the label for the name filter
  const nameFilterLabel = nameSearchTerm || nameFilterPlaceholder;

  return (
    <FiltersSection>
      <FiltersContainer>
        <FilterIcon width={24} height={24} />

        {showFilters.date && (
          <DateFilter
            // placeholder={[t("COMMON.CHECK_IN"), t("COMMON.CHECK_OUT")]}
            placeholder={DateFilterPlaceholder as [string, string]}
            value={filters.dateRange}
            onChange={handleDateRangeChange}
            active={filters.dateRange !== null}
          />
        )}

        {showFilters.channel && (
          <ChannelFilter
            // label={t("COMMON.CHANNEL")}
            label={ChannelFilterLabel as string}
            channels={channels}
            selectedChannels={filters.selectedChannels}
            onSelect={handleChannelsChange}
            isOpen={isChannelFilterOpen}
            onToggle={() => setIsChannelFilterOpen(!isChannelFilterOpen)}
          />
        )}

        {showFilters.name && (
          <NameFilter
            label={nameFilterLabel ?? nameFilterPlaceholder ?? ""}
            selectedNames={filters.selectedNames}
            onSelect={handleNamesChange}
            onSearchChange={handleNameSearchChange}
            isOpen={isNameFilterOpen}
            onToggle={() => setIsNameFilterOpen(!isNameFilterOpen)}
            resetSearchTerm={resetNameSearch}
          />
        )}

        {showFilters.price && (
          <PriceRangeFilter
            // label={t("COMMON.TOTAL_PRICE")}
            label={PriceRangeFilterLabel as string}
            selectedRange={filters.priceRange || [0, 50000]}
            onSelect={handlePriceRangeChange}
            min={0}
            max={50000}
            step={100}
            isOpen={isPriceRangeFilterOpen}
            onToggle={() => setIsPriceRangeFilterOpen(!isPriceRangeFilterOpen)}
          />
        )}
        {showFilters.enum && (
          <EnumFilter
            // label={t("COMMON.STATUS")}
            label={EnumFilterLabel as string}
            enumOptions={enumOptions}
            selectedValues={filters.enum}
            onSelect={handleEnumChange}
            isOpen={isEnumFilterOpen}
            onToggle={() => setIsEnumFilterOpen(!isEnumFilterOpen)}
          />
        )}
      </FiltersContainer>

      {isAnyFilterActive ? (
        <ClearFiltersContainer>
          <ClearFiltersText
            onClick={handleClearFilters}
            style={{
              color: isAnyFilterActive ? "#5B3FFF" : "#435363b8",
            }}
          >
            {t("COMMON.CANCEL")}
          </ClearFiltersText>
          <CloseIcon
            onClick={handleClearFilters}
            style={{
              cursor: "pointer",
              fill: isAnyFilterActive ? "#5B3FFF" : "#435363b8",
            }}
            width={10}
            height={10}
          />
        </ClearFiltersContainer>
      ) : null}
    </FiltersSection>
  );
};

export default ReservationFilters;
